import { useCallback, useEffect, useMemo, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useStore } from 'effector-react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { type GeneralID, SelectStatus } from '@types';
import TextLink from '@components/TextLink';
import Checkbox from '@components/Checkbox';
import getSelectedStatus from '@utils/getSelectedStatus';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { showConfirmModal } from '@components/ConfirmModal';
import { deleteScene } from '@modules/category/actions/deleteActions';
import { showScenesCopyModal } from '@modules/category/ScenesCopyModal';
import { $scenesManagementSearchResult, type SMResultItem } from '../stores';
import { SMDndType } from '../dnd';
import { ResultItem } from './ResultItem';

export function ResultList() {
  const sceneList = useStore($scenesManagementSearchResult);
  const [selected, setSelected] = useState<SMResultItem[]>([]);
  const [parent] = useAutoAnimate();
  const [activeItem, setActiveItem] = useState<SMResultItem | undefined>();
  const selectedIds = useMemo(() => selected.map((x) => x.id), [selected]);

  useEffect(() => {
    setSelected([]);
  }, [sceneList]);

  const dragItems = useMemo(() => {
    if (!activeItem) return [];
    if (selectedIds.includes(activeItem.id)) {
      return selected;
    }
    return [activeItem];
  }, [activeItem, selected]);

  const dragItemIds = useMemo(() => dragItems.map((x) => x.id), [dragItems]);

  const [{ isDragging }, drag, preview] = useDrag({
    type: SMDndType.scenes,
    item: {
      ids: dragItems.map((x) => ({
        id: x.id,
        parentId: x.categories[x.categories.length - 1].id,
      })),
      includeSelected: selectedIds.includes(activeItem?.id as GeneralID),
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: false });
  }, []);

  const handleDelete = async () => {
    const { confirmed } = await showConfirmModal({
      title: `确认删除${selected.length}个场景`,
      content: '所选场景下所有设置也都将会被删除，确认删除？',
    });
    if (confirmed) {
      deleteScene(
        selected.map((x) => ({
          sceneId: x.id,
          categoryId: x.categories[x.categories.length - 1].id,
        }))
      );
    }
  };

  const handleItemDelete = useCallback(
    async (item: SMResultItem) => {
      const { confirmed } = await showConfirmModal({
        title: '确认删除场景',
        content: `场景${item.name}下所有设置也都将会被删除，确认删除？`,
      });
      if (confirmed) {
        deleteScene([
          {
            sceneId: item.id,
            categoryId: item.categories[item.categories.length - 1].id,
          },
        ]);
      }
    },
    [setSelected]
  );

  const handleItemClick = useCallback(
    async (item: SMResultItem, isSelected: boolean) => {
      if (isSelected) {
        setSelected((prev) => [...prev, item]);
      } else {
        setSelected((prev) => prev.filter((x) => x.id !== item.id));
      }
    },
    [setSelected]
  );

  return (
    <div className="relative grid grid-rows-[50px_1fr] border-l">
      <div className="relative flex items-center bg-[#f8f8f8] px-20">
        <Checkbox
          label="全选"
          status={getSelectedStatus(selected.length, sceneList.length)}
          onClick={(status) => {
            if (status === SelectStatus.all) {
              setSelected([]);
            } else {
              setSelected(sceneList);
            }
          }}
        />
        <div className="ml-auto flex items-center gap-20">
          <TextLink
            disabled={!selected.length}
            onClick={() => {
              showScenesCopyModal({
                level: '场景',
                sceneIds: selected.map((x) => x.id),
              });
            }}
          >
            跨作品复制
          </TextLink>
          <TextLink disabled={!selected.length} onClick={handleDelete}>
            批量删除
          </TextLink>
        </div>
      </div>
      {sceneList.length ? (
        <Scrollbars>
          <div className="relative" ref={drag}>
            <div ref={parent}>
              {sceneList.map((x) => {
                return (
                  <ResultItem
                    key={x.id}
                    data={x}
                    isSelected={selectedIds.includes(x.id)}
                    isDragging={isDragging}
                    isThisDragging={isDragging && dragItemIds.includes(x.id)}
                    onActive={setActiveItem}
                    onClick={handleItemClick}
                    onDelete={handleItemDelete}
                  />
                );
              })}
            </div>
          </div>
        </Scrollbars>
      ) : (
        <div className="flex items-center justify-center">
          <div className="text-lg text-black-gray">暂无搜索结果</div>
        </div>
      )}
    </div>
  );
}

export default ResultList;
